const DEFAULT_MATCH_FIELDS = Object.freeze([
	'remarkNickName',
	'showNickName',
	'nickName',
	'showName',
	'showGroupName',
	'name'
]);

const DEFAULT_ACCOUNT_FIELDS = Object.freeze([
	'userName'
]);

export const CONTACT_SEARCH_TEXT_KEY = '__contactSearchText';

function toSearchText(value) {
	return String(value || '').trim().toLowerCase();
}

function readFieldValue(source, field) {
	if (!source || !field) {
		return '';
	}
	if (typeof field === 'function') {
		return field(source);
	}
	return source[field];
}

function toSearchValues(value) {
	if (Array.isArray(value)) {
		return value;
	}
	return [value];
}

export function normalizeSearchKeyword(keyword) {
	return toSearchText(keyword);
}

export function buildContactSearchText(source, extraFields = []) {
	return buildContactSearchIndex(source, extraFields).displayText;
}

export function buildContactSearchIndex(source, extraFields = [], accountFields = DEFAULT_ACCOUNT_FIELDS) {
	const fields = DEFAULT_MATCH_FIELDS.concat(extraFields || []);
	const displayText = fields
		.flatMap((field) => toSearchValues(readFieldValue(source, field)))
		.map((value) => toSearchText(value))
		.filter(Boolean)
		.join('\n');
	const accountValues = (accountFields || [])
		.flatMap((field) => toSearchValues(readFieldValue(source, field)))
		.map((value) => toSearchText(value))
		.filter(Boolean);
	return {
		displayText,
		accountValues
	};
}

function normalizePreparedSearchIndex(searchIndex) {
	if (!searchIndex) {
		return {
			displayText: '',
			accountValues: []
		};
	}
	if (typeof searchIndex === 'string') {
		return {
			displayText: toSearchText(searchIndex),
			accountValues: []
		};
	}
	if (typeof searchIndex !== 'object') {
		return {
			displayText: toSearchText(searchIndex),
			accountValues: []
		};
	}
	return {
		displayText: toSearchText(searchIndex.displayText),
		accountValues: Array.isArray(searchIndex.accountValues)
			? searchIndex.accountValues.map((value) => toSearchText(value)).filter(Boolean)
			: []
	};
}

export function mergeContactSearchIndexes(...searchIndexes) {
	const displayTexts = [];
	const accountValues = [];
	const seenDisplayText = new Set();
	const seenAccountValue = new Set();
	searchIndexes.forEach((searchIndex) => {
		const normalized = normalizePreparedSearchIndex(searchIndex);
		if (normalized.displayText && !seenDisplayText.has(normalized.displayText)) {
			seenDisplayText.add(normalized.displayText);
			displayTexts.push(normalized.displayText);
		}
		normalized.accountValues.forEach((accountValue) => {
			if (seenAccountValue.has(accountValue)) {
				return;
			}
			seenAccountValue.add(accountValue);
			accountValues.push(accountValue);
		});
	});
	return {
		displayText: displayTexts.join('\n'),
		accountValues
	};
}

export function attachContactSearchText(source, extraFields = []) {
	if (!source || typeof source !== 'object') {
		return source;
	}
	Object.defineProperty(source, CONTACT_SEARCH_TEXT_KEY, {
		value: buildContactSearchIndex(source, extraFields),
		enumerable: false,
		writable: true,
		configurable: true
	});
	return source;
}

export function matchesKeywordText(value, keyword) {
	const normalizedKeyword = normalizeSearchKeyword(keyword);
	if (!normalizedKeyword) {
		return true;
	}
	return toSearchText(value).includes(normalizedKeyword);
}

export function matchesPreparedContactKeyword(searchText, keyword) {
	const normalizedKeyword = normalizeSearchKeyword(keyword);
	if (!normalizedKeyword) {
		return true;
	}
	const searchIndex = normalizePreparedSearchIndex(searchText);
	return searchIndex.displayText.includes(normalizedKeyword)
		|| searchIndex.accountValues.some((accountValue) => accountValue === normalizedKeyword);
}

export function matchesContactKeyword(source, keyword, extraFields = []) {
	const normalizedKeyword = normalizeSearchKeyword(keyword);
	if (!normalizedKeyword) {
		return true;
	}
	if ((!Array.isArray(extraFields) || extraFields.length === 0) && source && source[CONTACT_SEARCH_TEXT_KEY]) {
		return matchesPreparedContactKeyword(source[CONTACT_SEARCH_TEXT_KEY], normalizedKeyword);
	}
	return matchesPreparedContactKeyword(buildContactSearchIndex(source, extraFields), normalizedKeyword);
}
